import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CoordinationResultScheduleDAndH } from './models/coordination-result-schedule-d-and-h';
import { FrequencyFormattingService } from '../../../../../../core/services/frequency-formatting/frequency-formatting.service';
import { GeoCoordinateSystemService } from '../../../../../../core/services/geo-coordinate-system/geo-coordinate-system.service';
import { EmissionDesignatorSortingService } from '../../../../shared/services/emission-designator-sorting/emission-designator-sorting.service';
import { EmissionDesignatorSortModel } from '../../../../shared/services/emission-designator-sorting/models/emission-designator-sort-model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Parallel } from '../../../../../../core/models/parallel';
import { CsvCustomContent } from 'ag-grid-community';

@Component({
    selector: 'app-coordination-result-schedule-d-and-h-info',
    imports: [MatTooltipModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './coordination-result-schedule-d-and-h-info.component.html',
})
export class CoordinationResultScheduleDAndHInfoComponent {
    @Input() scheduleDAndH!: CoordinationResultScheduleDAndH;

    NOT_APPLICABLE_TEXT = 'N/A';
    MAX_EMISSION_DESIGNATORS_SHOWN = 3;

    constructor(
        private readonly frequencyFormattingService: FrequencyFormattingService,
        private readonly geoCoordinateSystemService: GeoCoordinateSystemService,
        private readonly emissionDesignatorSortingService: EmissionDesignatorSortingService,
    ) {}

    getLatitudeLabel(): string {
        return this.getFormattedDMS(
            this.scheduleDAndH.location.latitude,
            'latitude',
        );
    }

    getLongitudeLabel(): string {
        return this.getFormattedDMS(
            this.scheduleDAndH.location.longitude,
            'longitude',
        );
    }

    getFrequencyLabel(): string {
        return this.frequencyFormattingService.getFrequencyLabel(
            this.scheduleDAndH.frequency.frequencyLow,
            this.scheduleDAndH.frequency.frequencyHigh,
        );
    }

    getRadiusLabel(): string {
        return (
            this.scheduleDAndH.location.radius?.toString() ??
            this.NOT_APPLICABLE_TEXT
        );
    }

    getAntennaHeightLabel(): string {
        return (
            this.scheduleDAndH.antenna.height?.toString() ??
            this.NOT_APPLICABLE_TEXT
        );
    }

    getEmissionDesignatorsLabel(): string {
        const codes = this.getSortedEmissionDesignatorCodes();

        if (this.needsEmissionDesignatorsTooltip()) {
            const items = codes.slice(0, this.MAX_EMISSION_DESIGNATORS_SHOWN);
            items.push('...');

            return items.join(', ');
        }

        return codes.join(', ');
    }

    getEmissionDesignatorsTooltip(): string {
        return this.getSortedEmissionDesignatorCodes().join(', ');
    }

    needsEmissionDesignatorsTooltip(): boolean {
        return (
            this.scheduleDAndH.frequency.emissionDesignators.length >
            this.MAX_EMISSION_DESIGNATORS_SHOWN
        );
    }

    getContentForExport(): CsvCustomContent {
        return [
            [
                {
                    data: {
                        value: `Latitude: ${this.getLatitudeLabel()}`,
                    },
                    mergeAcross: 3,
                },
                {
                    data: {
                        value: `Longitude: ${this.getLongitudeLabel()}`,
                    },
                },
            ],
            [
                {
                    data: {
                        value: `Frequency: ${this.getFrequencyLabel()}`,
                    },
                    mergeAcross: 3,
                },
                {
                    data: {
                        value: `Radius: ${this.getRadiusLabel()}`,
                    },
                },
            ],
            [
                {
                    data: {
                        value: `Antenna Height (m): ${this.getAntennaHeightLabel()}`,
                    },
                },
            ],
            [
                {
                    data: {
                        value: `Emission Designators: ${this.getEmissionDesignatorsLabel()}`,
                    },
                },
            ],
            [],
        ];
    }

    private getSortedEmissionDesignatorCodes(): string[] {
        return this.emissionDesignatorSortingService
            .sortByBandwidthDescending(
                this.scheduleDAndH.frequency.emissionDesignators,
            )
            .map(
                (emissionDesignator: EmissionDesignatorSortModel) =>
                    emissionDesignator.emissionDesignatorCode,
            );
    }

    private getFormattedDMS(decimal: number | null, type: Parallel): string {
        const formattedDMS = this.geoCoordinateSystemService.getFormattedDMS(
            decimal,
            type,
        );

        if (!formattedDMS) {
            return this.NOT_APPLICABLE_TEXT;
        }

        return formattedDMS;
    }
}
